import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { MindyKnowledgePermissions } from '@quadminds/shared/mindy';
import { useQueryClient } from '@tanstack/react-query';
import { SortingState, VisibilityState } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useByPassContext } from '../providers/ByPass/ByPass.provider';
import { agentsApi } from '../services/agents-api.service';
import { FileItem } from '../types/File/File.types';

interface UseTableProps {
    readonly draggedFileId: string | null;
    readonly onInternalDragStart: (fileId: string) => void;
    readonly onInternalDragEnd: () => void;
    readonly onMoveFile: (fileId: string, folderId: string) => void;
    readonly onToggleActive: (file: FileItem, newState: boolean) => void;
    readonly onNavigate: (folderId: string) => void;
    readonly onPreviewFile: (file: FileItem) => void;
    readonly files: FileItem[];
    readonly selectedItems: string[];
}

const useTable = ({
    draggedFileId,
    onInternalDragStart,
    onInternalDragEnd,
    onMoveFile,
    onToggleActive,
    onNavigate,
    onPreviewFile,
    files,
    selectedItems,
}: UseTableProps) => {
    const { t } = useTranslation();
    const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }]);
    const [dropTargets, setDropTargets] = useState<Record<string, boolean>>({});
    const containerRef = useRef<HTMLDivElement>(null);
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
        comments: false,
    });

    const queryClient = useQueryClient();
    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);
    const currentUserPermissions = useMemo(() => userInfo?.permissions || [], [userInfo]);
    const { isImpersonated } = useByPassContext();

    const handleDragStart = (e: React.DragEvent, file: FileItem) => {
        if (file.type === 'file') {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData(
                'application/json',
                JSON.stringify({
                    fileId: file.id,
                    fileName: file.name,
                    fileType: file.type,
                }),
            );
            onInternalDragStart(file.id);
        }
    };

    const handleDragEnd = () => {
        onInternalDragEnd();
    };

    const handleDragOver = (e: React.DragEvent, folder: FileItem) => {
        e.preventDefault();
        e.stopPropagation();

        if (folder.type === 'folder' && folder.id !== draggedFileId) {
            e.dataTransfer.dropEffect = 'move';
            setDropTargets((prev) => ({ ...prev, [folder.id]: true }));
        }
    };

    const handleDragLeave = (e: React.DragEvent, folderId: string) => {
        e.preventDefault();
        e.stopPropagation();
        setDropTargets((prev) => ({ ...prev, [folderId]: false }));
    };

    useEffect(() => {
        if (!draggedFileId) {
            setDropTargets({});
        }
    }, [draggedFileId]);

    const handleDrop = (e: React.DragEvent, folder: FileItem) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            const jsonData = e.dataTransfer.getData('application/json');
            if (jsonData) {
                const data = JSON.parse(jsonData);

                if (data.fileId && folder.type === 'folder') {
                    onMoveFile(data.fileId, folder.id);
                }
            }
        } catch (error) {
            console.error('Error parsing drag data:', error);
        }

        setDropTargets({});
    };

    const handleSwitchChange = useCallback(
        (e: React.MouseEvent, file: FileItem) => {
            e.stopPropagation();
            onToggleActive(file, !file.active);
        },
        [onToggleActive],
    );

    const handleRowClick = (e: React.MouseEvent, file: FileItem) => {
        if (file.type === 'folder') {
            onNavigate(file.id);
            return;
        }

        if (
            !(e.target as HTMLElement).closest('input[type="checkbox"]') &&
            !(e.target as HTMLElement).closest('.switch-container') &&
            !(e.target as HTMLElement).closest('.actions-container')
        ) {
            onPreviewFile(file);
        }
    };

    const allSelected = files.length > 0 && files.every((file) => selectedItems.includes(file.id));
    const showActions = currentUserPermissions.includes(MindyKnowledgePermissions.UpdateKnowledge) || isImpersonated;
    const hasToDisableDelete =
        !isImpersonated && !currentUserPermissions.includes(MindyKnowledgePermissions.DeleteKnowledge);

    const handlePrefetchPermissions = (fileId: string) => {
        queryClient.prefetchQuery({
            queryKey: ['document-permissions', fileId],
            queryFn: () => agentsApi.getUsersPerDocument(fileId),
            staleTime: 1000 * 60 * 5,
        });
    };

    return {
        t,
        sorting,
        setSorting,
        dropTargets,
        setDropTargets,
        containerRef,
        columnVisibility,
        setColumnVisibility,
        currentUserPermissions,
        isImpersonated,
        handleDragStart,
        handleDragEnd,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleSwitchChange,
        handleRowClick,
        onNavigate,
        allSelected,
        showActions,
        hasToDisableDelete,
        handlePrefetchPermissions,
    };
};

export default useTable;
